// src/tenant-loader/TenantLoader.js - Centralized tenant loading and configuration

// Supported tenants configuration
const SUPPORTED_TENANTS = ['inspect', 'appesteem'];
const DEFAULT_TENANT = 'inspect';

// Function to extract tenant ID from URL
const getTenantIdFromUrl = () => {
  const hostname = window.location.hostname;
  const urlParams = new URLSearchParams(window.location.search);
  
  let tenantId;
  
  // For localhost development
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    tenantId = urlParams.get('tenantId');
    //check if tenantId is empty and then assign default tenant
    if (!tenantId) {
      tenantId = DEFAULT_TENANT;
    }
  }
  // For production subdomain pattern: {tenantId}.threeclicks.ai
  else if (hostname.endsWith('.threeclicks.ai')) {
    tenantId = hostname.split('.')[0];
  }
  else if (hostname.endsWith('.inspectorclick.com')) {
    tenantId = hostname.split('.')[0];
  }
  
  // Validate tenant ID - NO DEFAULT FALLBACK
  if (tenantId && SUPPORTED_TENANTS.includes(tenantId)) {
    return tenantId;
  }
  
  // Return null instead of DEFAULT_TENANT
  return null;
};

// Determine tenant ID dynamically
export const TENANT_ID = getTenantIdFromUrl();

// Function to load tenant config asynchronously
export const loadTenantConfig = async () => {
  try {
    const config = await import(`../tenant/${TENANT_ID}/config.js`);
    return config.TENANT_CONFIG;
  } catch (error) {
    console.error(`Failed to load config for tenant ${TENANT_ID}:`, error);
    // Fallback to default config
    const defaultConfig = await import(`../tenant/${DEFAULT_TENANT}/config.js`);
    return defaultConfig.TENANT_CONFIG;
  }
};

// Simple error component
const TenantErrorComponent = () => {
  const React = require('react');
  const { Box, Typography, Button } = require('@mui/material');
  
  return (
    <Box sx={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center', 
      height: '100vh',
      flexDirection: 'column',
      backgroundColor: 'background.default'
    }}>
      <Typography variant="h4" color="error" sx={{ mb: 2 }}>
        Unknown Error
      </Typography>
      <Typography variant="body1" sx={{ mb: 2, textAlign: 'center' }}>
        Some unknown error happened. Please reload to fix this.
      </Typography>
      <Button 
        variant="contained"
        onClick={() => window.location.reload()}
      >
        Reload Page
      </Button>
    </Box>
  );
};

// Function to load tenant app lazily (for React Suspense)
export const loadTenantApp = async () => {
  try {
    const tenantApp = await import(`../tenant/${TENANT_ID}/App.js`);
    return tenantApp;
  } catch (error) {
    console.error(`Failed to load tenant ${TENANT_ID}:`, error);
    // Return error component instead of fallback
    return {default: TenantErrorComponent};
  }
};

// Function to load tenant Firebase config synchronously
export const loadTenantFirebase = () => {
  try {
    const tenantFirebase = require(`../tenant/${TENANT_ID}/firebase`);
    return tenantFirebase;
  } catch (error) {
    console.error(`❌ Failed to load Firebase config for tenant: ${TENANT_ID}`);
    console.error(`Error:`, error.message);
    throw new Error(`Tenant '${TENANT_ID}' Firebase config not found. Please ensure the tenant is properly configured.`);
  }
};

// Function to load tenant AuthContext synchronously
export const loadTenantAuthContext = () => {
  try {
    const tenantAuthContext = require(`../tenant/${TENANT_ID}/AuthContext`);
    return tenantAuthContext;
  } catch (error) {
    console.error(`❌ Failed to load AuthContext for tenant: ${TENANT_ID}`);
    console.error(`Error:`, error.message);
    throw new Error(`Tenant '${TENANT_ID}' AuthContext not found. Please ensure the tenant is properly configured and all required files exist.`);
  }
};

// Export tenant constants
export { SUPPORTED_TENANTS, DEFAULT_TENANT };
